import { useEffect, useState, useCallback } from 'react';
import { Link, Outlet, useLocation, useMatches, useNavigate } from 'react-router-dom';
import { twJoin } from 'tailwind-merge';
import { Breadcrumb, BreadcrumbItem } from '@/components/shared/breadcrumb/Breadcrumb';
import { ExtendedSidebar } from '@/components/shared/extendedSidebar/ExtendedSidebar';
import useAuthState from '@/states/auth.state';
import useTenantState from '@/states/tenant.state';
import useCompanyDataState from '@/states/company-data.state';
import useErrorState from '@/states/error.state';
import { Header } from '@/components/shared/header/Header';
import useWebSocket from '@/hooks/useWebsocket';
import { useNotificationWebSocket } from '@/hooks/useNotificationWebSocket';
import { NotificationContent } from './NotificationContent';
import useNotificationState from '@/states/notification.state';
import { ToastContainer, toast } from 'react-toastify';
import { ExtendedMenuConfig } from '@/utils/configs/menu';
import { useWindowSize } from '@/hooks/useWindowSize';
import { BREAKPOINTS } from '@/utils/constants/breakpoints';
import { useTenantWatcher } from '@/hooks/useTenantWatcher';
import { useGetCompanyData } from '@/pages/company-profile/company-data/useCompanyData';
import { useCompletionStatus } from '@/hooks/useCompletionStatus';
import { AdminRoleGuard } from '@/components/shared/gate/AdminRoleGuard';
import { env } from '@/utils/constants/env';
import { TrialExpirationAlert } from '@/components/shared/TrialExpirationAlert';
import { SubscriptionExpiredOverlay } from '@/components/shared/SubscriptionExpiredOverlay';
import { isSubscriptionExpired } from '@/utils/subscription';
import { OrgChartProvider } from '@/contexts/OrgChartContext';

export const MenuWrapper = (props: { children: JSX.Element; menu: ExtendedMenuConfig; toggled: boolean }) => {
  const { children, menu, toggled } = props;

  if (menu.disabled) {
    return (
      <div className="w-full text-left cursor-not-allowed opacity-50 select-none" onClick={e => e.preventDefault()}>
        {children}
      </div>
    );
  }

  if (menu.type === 'label') {
    if (toggled) {
      return <hr className="my-2 mx-4 border-gray-200 dark:border-gray-700" />;
    }
    if (menu.sticky) {
      return (
        <div className="sticky bottom-0 z-10 px-4 pt-3 pb-2 bg-[#FAFAFA] dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2 rounded-lg bg-[#004EEB] px-3 py-2">
            <span className="text-white">{menu.icon}</span>
            <span className="text-xs font-bold tracking-wider text-white">{menu.label}</span>
          </div>
        </div>
      );
    }
    return (
      <div className="mt-4 px-6 py-1.5 text-xs text-gray-400 dark:text-gray-500 font-bold tracking-wider uppercase">
        {menu.label}
      </div>
    );
  }
  if (menu.onClick) {
    return (
      <button onClick={menu.onClick} className="w-full text-left">
        {children}
      </button>
    );
  }

  return (
    <Link to={menu.href ?? '#'} target={menu.newPage ? '_blank' : '_self'}>
      {children}
    </Link>
  );
};

interface RouteHandle {
  breadcrumb?: string;
  category?: string;
}

const useBreadcrumbs = (): BreadcrumbItem[] => {
  const matches = useMatches();

  return matches
    .filter((match): match is typeof match & { handle: RouteHandle } =>
      Boolean((match.handle as RouteHandle)?.breadcrumb),
    )
    .map(match => ({
      label: (match.handle as RouteHandle).breadcrumb!,
      path: match.pathname,
      category: (match.handle as RouteHandle).category,
    }));
};

interface AvatarContentProps {
  isSuperAdmin: boolean;
  tenantUuid?: string;
  onSignOut: () => void;
}

const AvatarContent = ({ isSuperAdmin, tenantUuid, onSignOut }: AvatarContentProps) => (
  <div className="p-2 bg-white dark:bg-gray-800 rounded-lg min-w-[140px]">
    {isSuperAdmin ? (
      <a
        href={`${env.apiBaseUrl}/admin`}
        className="block w-full p-2 text-left rounded cursor-pointer text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 mb-1"
      >
        Admin Panel
      </a>
    ) : (
      <a
        href={`${env.apiBaseUrl}/dashboard/${tenantUuid}/my-profile`}
        className="block w-full p-2 text-left rounded cursor-pointer text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 mb-1"
      >
        Profil Saya
      </a>
    )}
    <button
      className="w-full p-2 text-left rounded cursor-pointer text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
      onClick={onSignOut}
    >
      Sign Out
    </button>
  </div>
);

export const AdminLayout = () => {
  const { userProfile, isSuperAdmin } = useAuthState();
  const { logout } = useAuthState();
  const { removeCurrentTenant, currentTenant } = useTenantState();
  const { setHasCompanyData, setIsLoading, setIsCompanyProfileComplete } = useCompanyDataState();
  const { width } = useWindowSize();
  const [toggled, setToggled] = useState(width < BREAKPOINTS.MD);
  const location = useLocation();
  useTenantWatcher();

  const { data: companyData, isLoading: isLoadingCompanyData } = useGetCompanyData(currentTenant?.id);
  const { data: completionStatus, isLoading: isLoadingCompletionStatus } = useCompletionStatus(currentTenant?.id);

  useEffect(() => {
    if (!currentTenant?.id) return;

    // Both queries must finish before we consider loading done.
    // This prevents guards from redirecting while completionStatus is still in-flight.
    const isStillLoading = isLoadingCompanyData || isLoadingCompletionStatus;
    setIsLoading(isStillLoading);
    setHasCompanyData(!!companyData);

    // Update completion status from React Query
    if (completionStatus) {
      setIsCompanyProfileComplete(completionStatus.is_fully_complete);
    }
  }, [
    companyData,
    isLoadingCompanyData,
    isLoadingCompletionStatus,
    completionStatus,
    setHasCompanyData,
    setIsLoading,
    currentTenant,
    setIsCompanyProfileComplete,
  ]);

  useEffect(() => {
    setToggled(width < BREAKPOINTS.MD);
  }, [width]);

  // New notification system
  const { fetchNotifications, fetchUnreadCount } = useNotificationState();

  // Initialize notification polling for updates (every 30 seconds)
  useNotificationWebSocket({
    enabled: !!currentTenant?.id,
    pollingInterval: 30000,
  });

  // Legacy WebSocket for backward compatibility
  const userData = {
    username: userProfile?.email,
  };

  const handleLegacyNotification = useCallback(
    (notif: string) => {
      if (userData.username && currentTenant?.id) {
        // Refresh new notification system
        fetchNotifications(currentTenant.id);
        fetchUnreadCount(currentTenant.id);
        // Show legacy toast
        try {
          const data = JSON.parse(notif);
          toast.success(`${data.subject}`);
        } catch (e) {
          console.error('Failed to parse legacy notification:', e);
        }
      }
    },
    [userData.username, currentTenant?.id, fetchNotifications, fetchUnreadCount],
  );

  useWebSocket(userData, handleLegacyNotification, () => {
    return;
  });

  const { has403Error, clear403Error } = useErrorState();

  useEffect(() => {
    // Clear 403 error when route changes
    clear403Error();
  }, [location.pathname, clear403Error]);

  const navigate = useNavigate();
  // ... (inside component)

  const renderContent = () => {
    if (has403Error) {
      return (
        <div className="flex items-center justify-center min-h-[70vh] w-full">
          <div className="text-center px-6">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-red-100 mb-6">
              <svg
                className="w-12 h-12 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-3">Access Denied</h1>
            <p className="text-lg text-gray-600 mb-6 max-w-md mx-auto">
              You don't have permission to access this page. Please contact your administrator if you believe this is an
              error.
            </p>
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              Go Back
            </button>
          </div>
        </div>
      );
    }

    if (!isSuperAdmin && currentTenant && isSubscriptionExpired(currentTenant)) {
      return <SubscriptionExpiredOverlay />;
    }

    return <Outlet key={currentTenant?.id} />;
  };

  useEffect(() => {
    const TOAST_ID = 'global-toast';

    // Hindari stringifikasi default '[object Object]': hanya konversi nilai
    // primitif ke string; biarkan ReactNode (objek/elemen) apa adanya.
    const toMessageString = (message: unknown): string =>
      typeof message === 'string' || typeof message === 'number' ? String(message) : '';

    const originalSuccess = toast.success;
    const originalError = toast.error;
    const originalInfo = toast.info;
    const originalWarning = toast.warning;

    const replaceToast = (
      originalFn: typeof toast.success,
      type: 'success' | 'error' | 'info' | 'warning',
      message: string,
      options?: object,
    ) => {
      if (toast.isActive(TOAST_ID)) {
        toast.update(TOAST_ID, {
          render: message,
          type,
          autoClose: 3000,
          ...options,
        });

        return TOAST_ID;
      }

      return originalFn(message, {
        toastId: TOAST_ID,
        autoClose: 3000,
        ...options,
      });
    };

    toast.success = (message, options) => replaceToast(originalSuccess, 'success', toMessageString(message), options);
    toast.error = (message, options) => replaceToast(originalError, 'error', toMessageString(message), options);
    toast.info = (message, options) => replaceToast(originalInfo, 'info', toMessageString(message), options);
    toast.warning = (message, options) => replaceToast(originalWarning, 'warning', toMessageString(message), options);

    return () => {
      toast.success = originalSuccess;
      toast.error = originalError;
      toast.info = originalInfo;
      toast.warning = originalWarning;
    };
  }, []);

  return (
    <div className="max-w-screen h-screen flex flex-col overflow-hidden dark:text-white">
      <div className="flex-shrink-0 z-30">
        <Header
          toggled={toggled}
          setToggled={setToggled}
          avatarContent={
            <AvatarContent
              isSuperAdmin={isSuperAdmin}
              tenantUuid={currentTenant?.uuid}
              onSignOut={() => {
                removeCurrentTenant();
                logout();
              }}
            />
          }
          notificationContent={<NotificationContent />}
        />
      </div>
      <OrgChartProvider tenantId={currentTenant?.id}>
        <ExtendedSidebar toggled={toggled} setToggled={setToggled} menuWrapper={MenuWrapper} />

        <div
          className={twJoin(
            'flex-1 overflow-y-auto transition-[margin] ease-in-out duration-400 ',
            toggled ? 'md:ml-[80px]' : 'md:ml-[238px]',
          )}
        >
          <Breadcrumb items={useBreadcrumbs()} onToggle={() => setToggled(prev => !prev)} toggled={toggled} />
          <main className="flex flex-col items-center hex-bg min-h-screen">
            <AdminRoleGuard>{renderContent()}</AdminRoleGuard>
          </main>
        </div>
      </OrgChartProvider>

      <TrialExpirationAlert />
      <ToastContainer position="top-right" newestOnTop limit={1} />
    </div>
  );
};
